/**
 * English. Structurally identical to pl.ts — the type in ../index.ts is derived
 * from the Polish tree, so a key added here and not there (or the reverse) is a
 * type error rather than a string that renders as `offspring.newThing` in one
 * language.
 *
 * Keys are species-neutral (`tenant`, `offspring`); the values are not, and must
 * not be. This is the seam that lets the same code serve a stud farm one day —
 * a second vertical is a second phrase tree, not a second data model.
 */
export const en = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    back: 'Back',
    close: 'Close',
    loading: 'Loading…',
    offline: 'No connection',
    unsynced: 'Saved on this phone — not yet synced',
    tryAgain: 'Try again',
    optional: 'optional',
    someone: 'Someone in the kennel',
    // A past moment said the way a person says it: the day named, then the
    // clock. The day comes from `asRelativeDay` — "today", "yesterday", or a
    // date — and only the joining word is copy.
    dayAtTime: '{{day}} at {{time}}',
  },
  /** Chrome shared by the full-screen wizards — weighing and photographs. See pl.ts. */
  wizard: {
    changeWhen: 'Change date',
    dateLabel: 'Date',
    timeLabel: 'Time',
  },
  auth: {
    signIn: 'Sign in with Google',
    signOut: 'Sign out',
    tagline: 'Weights and health for newborn puppies, from day one.',
    signedInAs: 'Signed in as {{email}}',
    failed: 'Sign-in did not go through. Check your connection and try again.',
    signingIn: 'Signing in…',
  },
  start: {
    title: 'Start your own kennel',
    lead: 'A kennel is the shared place for your litters, weights and health history. You can invite family and your vet into it whenever you are ready.',
    nameLabel: 'Kennel name',
    namePlaceholder: 'Green Valley',
    breedLabel: 'Breed',
    breedPlaceholder: 'Welsh Corgi Pembroke',
    countryLabel: 'Country',
    submit: 'Create kennel',
    creating: 'Creating your kennel…',
    needsName: 'Give the kennel a name.',
    failed: 'The kennel could not be created. Try again.',
    takingLonger: 'This can take a little longer than usual. Please bear with it.',
  },
  invite: {
    waiting_one: 'An invitation is waiting for you',
    waiting_other: '{{count}} invitations are waiting for you',
    asRole: 'as {{role}}',
    accept: 'Join this kennel',
    accepting: 'Joining…',
    acceptFailed: 'You could not be added. The invitation may have expired — ask for a new one.',
    panelTitle: 'Invitations',
    add: 'Invite someone',
    emailLabel: 'Email address',
    emailPlaceholder: 'anna@example.com',
    roleLabel: 'Role',
    scopesLabel: 'What they see',
    needsScope: 'Tick at least one thing for them to see.',
    send: 'Invite',
    sending: 'Sending…',
    sendFailed: 'The invitation could not be created. Try again.',
    needsEmail: 'Enter an email address.',
    selfEmail: 'That is your own address.',
    hint: 'They join once they sign in with Google using that same address.',
    none: 'Nobody is waiting to join.',
    revokeShort: 'Withdraw',
    sentAt: 'Invitation sent {{date}}',
    sendFailedStatus: 'The invitation could not be sent. Try again, or pass on the link.',
    sendFailedShort: 'Not sent',
    resend: 'Send again',
    resendFailed: 'It could not be sent. Check the address, or pass on the link.',
    copyLink: 'Copy link',
    linkCopied: 'Copied',
    revoke: 'Withdraw invitation',
    revokeTitle: 'Withdraw the invitation?',
    revokeBody: '{{email}} will not be able to join with it. You can send a new one.',
    expiresIn_one: 'Expires in {{count}} day',
    expiresIn_other: 'Expires in {{count}} days',
    expired: 'Expired',
  },
  /**
   * The screen an invitation link opens — see routes/join.$inviteId.tsx. Whoever
   * opens it usually has no account in this app yet.
   */
  join: {
    title: 'An invitation to a kennel',
    signInLead: 'Sign in to see the invitation and join the kennel.',
    sameAddress: 'Use the same email address the invitation was sent to.',
    missingTitle: 'That invitation could not be found',
    missingLead: 'There is no invitation here for {{email}}. It may have been withdrawn, it may have expired, or it may be addressed to a different email.',
    expiredLead: 'This invitation has expired. Ask for a new one.',
    useAnotherAccount: 'Sign in with another account',
    toApp: 'Go to the app',
  },
  member: {
    title: 'People',
    accountTitle: 'Your account',
    you: 'This is you',
    since: 'In the kennel since {{date}}',
    ownerOnly: 'Only an owner can invite people and change roles.',
    roleLabel: 'Role',
    changeRole: 'Change role',
    changeRoleDone: 'Done',
    remove: 'Remove',
    removeTitle: 'Remove {{name}} from the kennel?',
    removeBody: '{{name}} loses access to the litters, the weights and the health history. Recorded data stays. You can invite them again.',
    changeFailed: 'That could not be changed. A kennel must keep at least one owner.',
    empty: 'Nobody here yet.',
  },
  role: {
    owner: 'Owner',
    handler: 'Breeder',
    viewer: 'Observer',
    ownerWhat: 'Runs the kennel and its people',
    handlerWhat: 'Keeps the litters, weights and health',
    viewerWhat: 'Follows the parts of a litter you choose',
  },
  /**
   * What an observer is shown, one line per part of a litter's record. They are
   * the names of the two sections on the litter's page, so that the boxes an
   * owner ticks and the headings the observer ends up reading are the same
   * words. See `VIEW_SCOPES` in platform/src/roles.ts.
   */
  scope: {
    measurements: 'Weights and growth',
    photos: 'Photo timeline',
    tasks: 'Tasks',
  },
  nav: {
    litters: 'Litters',
    offspring: 'Puppies',
    people: 'People',
    breadcrumb: 'Breadcrumb',
  },
  /**
   * Collar colours. Written so they can stand alone in place of a name the
   * puppy does not have yet — the grid draws "Blue" where a name would go.
   */
  collar: {
    red: 'Red',
    orange: 'Orange',
    yellow: 'Yellow',
    green: 'Green',
    blue: 'Blue',
    violet: 'Violet',
    pink: 'Pink',
  },
  sex: {
    female: 'Female',
    male: 'Male',
  },
  litter: {
    title: 'Litters',
    add: 'New litter',
    addFirst: 'Add your first litter',
    empty: 'No litters yet',
    emptyHint: 'Start with a litter — then add the puppies in it, their weights and their health.',
    emptyReadOnly: 'Litters are started by an owner or a breeder.',
    readOnly: 'Only an owner or a breeder can start a litter.',
    newTitle: 'New litter',
    step: 'Step {{current}} of {{total}}',
    nameLabel: 'Litter name',
    namePlaceholder: 'Litter A',
    nameHint: 'How you will find it in the list — the litter letter, or the parents’ names.',
    needsName: 'Give the litter a name.',
    needsDate: 'Give a date.',
    statusLabel: 'Status',
    status: {
      active: 'Current',
      incoming: 'Expected',
      historical: 'Archived',
    },
    statusWhat: {
      active: 'The puppies are here and growing.',
      incoming: 'The bitch is in whelp — there are no puppies yet.',
      historical: 'Closed: every puppy has gone or stayed.',
    },
    dueLabel: 'Due date',
    bornLabel: 'Born',
    // Both halves of the moment, labelled for a screen reader and hidden on
    // screen: the `Born` legend above already names the pair. See `TextField`.
    bornDateLabel: 'Date of birth',
    bornTimeLabel: 'Time of birth',
    bornTimeHint:
      'The hour matters: the first two days are judged in 24-hour windows, and a date with no time is out by up to twelve of them. An approximate hour beats a precise midnight.',
    damLabel: 'Dam',
    damHint: 'Type her name. If she is not in the kennel yet, we add her.',
    damWeightLabel: 'Dam’s weight before the mating (kg)',
    damWeightPlaceholder: '12.4',
    damWeightHint:
      'What she weighed before she was in whelp. It is how we tell a low birth weight from a normal one — the first-week thresholds are much stricter for a small newborn.',
    needsDamWeight: 'Give a weight in kilograms, up to {{max}}.',
    sireLabel: 'Sire',
    sireHint: 'Type his name. If he is not in the kennel yet, we add him.',
    breedLabel: 'Breed',
    breedHint: 'Kept on the litter, so it stays what it was even if the kennel later changes breed.',
    countLabel: 'Number of puppies',
    countHint: 'That many cards open in the next step. You can add or remove them there.',
    countExpectedLabel: 'Puppies expected',
    countExpectedHint: 'Just the number — you add the puppies when they arrive.',
    countStoredHint: 'How many were expected. Compared against how many are here.',
    saveIncoming: 'Save litter',
    toOffspring: 'Next: the puppies',
    bornOn: 'Born {{date}}',
    dueOn: 'Due {{date}}',
    born: 'Born',
    due: 'Due',
    parents: 'Parents',
    countLabelShort: 'Puppies',
    countVsExpected: '{{count}} of {{expected}} expected',
    count_one: '{{count}} puppy',
    count_other: '{{count}} puppies',
    expected_one: '{{count}} expected',
    expected_other: '{{count}} expected',
    missing: 'There is no such litter.',
  },
  animal: {
    unknown: 'Not known',
    namePlaceholder: {
      female: 'Maya',
      male: 'Rex',
    },
  },
  offspring: {
    title: 'Puppies',
    empty: 'No puppies in this litter',
    // Where the board itself no longer says: adding a puppy lives behind the
    // pencil, with everything else that changes what this litter is. Shown only
    // to a reader who may edit — the rest get `empty` on its own.
    emptyWhere: 'Puppies are added in the litter’s editor — the pencil beside its name.',
    emptyIncoming: 'No puppies yet. Add them when they arrive.',
    missing: 'There is no such puppy.',
    unnamed: 'No name yet',
    add: 'Add',
    addAnother: 'Add another puppy',
    remove: 'Remove puppy',
    deleteTitle: 'Delete {{name}}?',
    deleteBody: 'Its weights, health events and photographs go with it. This cannot be undone.',
    edit: 'Edit {{name}}',
    open: 'Open {{name}}',
    ordinal: 'Puppy {{index}}',
    stepLead: 'A collar and a sex for each. The name and the photographs can wait.',
    sexLabel: 'Sex',
    statusLabel: 'Status',
    nameLabel: 'Name',
    namePlaceholder: 'Bella',
    nameHint: 'Leave it empty — you will know the puppy by its collar.',
    birthWeightLabel: 'Birth weight (g)',
    birthWeightHint:
      'What it weighed at birth, dried and before its first feed. Three of the growth checks are measured from it, and it is the one weight that cannot be taken again.',
    birthWeightIncomingHint: 'The puppies are not here yet — add it when they arrive.',
    birthWeightMissing: 'Not recorded',
    needsBirthWeight: 'Give the birth weight in grams.',
    collarLabel: 'Collar',
    collarHint: 'The colour you will know this puppy by, before it has a name.',
    collarTaken: 'already taken',
    needsCollar: 'Pick a collar colour.',
    needsSex: 'Mark the sex.',
    photoLabel: 'Main photograph',
    photoHint: 'How you will know this puppy in a list and beside its weights.',
    photoReplaceHint: 'Tap the photograph to pick a different one.',
    photoFailed: 'The photograph did not upload. The puppy saves without it — add one later.',
    photoUploading: 'Sending…',
    photoAdd: 'Add a photograph',
    photoReplace: 'Change photograph',
    photoRemove: 'Remove photograph',
    incomplete_one: '{{count}} puppy still needs a collar, a sex or a birth weight.',
    incomplete_other: '{{count}} puppies still need a collar, a sex or a birth weight.',
    saveAll_one: 'Save {{count}} puppy',
    saveAll_other: 'Save {{count}} puppies',
    status: {
      raising: 'Being raised',
      reserved: 'Reserved',
      placed: 'Gone to its home',
      kept: 'Staying here',
      deceased: 'Died',
    },
    ageDays_one: '{{count}} day',
    ageDays_other: '{{count}} days',
    weight: 'Weight',
    weightMissing: 'No weight yet',
    recordWeight: 'Record weight',
    losingWeight: 'Losing weight — check on this puppy',
    weighPuppies: 'Weigh puppies',
    weighTitle: 'Weighing puppies',
    weighProgress: '{{count}} of {{total}} weighed',
    weighPending: 'To weigh',
    weighed: 'Weighed',
    weighEdit: 'Edit the weight for {{name}}',
    weighUnitGrams: 'g',
    // The label on the reference under a typed weight: the last couple of
    // weighings this puppy already has, so the new number is checked against
    // them without leaving the session. Each is a weight and an elapsed gap,
    // never a date — see `Previously` in components/WeighWizard.tsx.
    weighPrevious: 'Before',
    weighInvalidGrams: 'Enter a weight in grams.',
    weighToday: 'Recorded as today’s weighing, at {{time}}.',
    weighBackdated: 'Recorded as a weighing from {{date}}, at {{time}}.',
    weighReviewTitle: 'Confirm the weights',
    weighReviewLead: 'Check each puppy before saving.',
    // Shown once a session covers part of the litter: what happens to the
    // puppies still without a number.
    weighPartialLead: 'The puppies with a weight are saved. Weigh the rest whenever they are back.',
    weighConfirm: 'Save all weights',
    // The Confirm on a session that stops short of the whole litter. The count
    // is the label, so how many weights are about to be written is read off the
    // button itself.
    weighConfirmSome_one: 'Save {{count}} weight',
    weighConfirmSome_other: 'Save {{count}} weights',
    weighDiscard: 'Discard without saving',
    weighLeaveTitle: 'Discard this weighing?',
    weighLeaveBody: 'The weights typed so far will be gone. Nothing has been saved yet.',
    weighLeaveCancel: 'Keep weighing',
  },
  /**
   * Growth monitoring: the chart and the table, on a litter's page and a
   * puppy's. `status.*` and `advice.*` mirror `GrowthStatus` one for one.
   */
  growth: {
    title: 'Growth',
    empty: 'Record the first weights to see growth.',
    view: {
      chart: 'Chart',
      table: 'Table',
    },
    day: 'Day {{day}}',
    dayShort: '{{day}} d',
    // The chart's first point and the table's last row: not a weighing but the
    // puppy's birth weight. It is an input to the model — three of its published
    // thresholds are measured from it — but it is never itself judged.
    birthWeight: 'Birth weight',
    grams: '{{grams}} g',
    delta: '{{grams}} g',
    percent: '{{percent}}%',
    // The number the verdict actually came from: a regression across every
    // weighing in the last 48 (or, after three weeks, 72) hours, as percent per
    // day. Deliberately NOT called "per day" any more — the old key meant the
    // difference between two weighings, and reusing the word for a trend would
    // have let one reading of the section be carried into another.
    trend: '{{percent}}%/day trend',
    // Which weighing a row's gain is measured from — the one above it, which for
    // a litter weighed twice a day may be eight hours back and not a day.
    since: 'since {{date}}',
    lastWeighed: 'last weighed {{date}}',
    // The two numbers a rule compared, under the sentence that quotes it.
    // "−4.7% against −4.1%" — the observed figure and the threshold, in the unit
    // the threshold is published in. See components/growth/reasons.ts.
    against: '{{observed}} against {{threshold}}',
    // What stands where a paper's name would, for a rule that has no paper.
    sourceHeuristic: 'rule of thumb, no published threshold',
    showBands: 'Show the reference range',
    wellAll: 'Every puppy is growing properly.',
    wellOne: 'Growing properly.',
    checkNow: 'Check now: {{names}}',
    checkNowOne: 'Check now.',
    watchThese: 'Keep an eye on: {{names}}',
    watchThisOne: 'Keep an eye on this one.',
    // A prompt, not a warning, and only while it would still change anything.
    // Without the dam's weight the model cannot tell a low birth weight from a
    // normal one, so it uses the gentler thresholds and says less.
    needsDamWeight:
      'Record what the dam weighed before the mating, in the litter settings. It is how we tell a low birth weight from a normal one — and the first-week thresholds are much stricter for a small newborn.',
    openLitter: 'Go to the litter',
    columnDay: 'Day',
    columnWeight: 'Weight (g)',
    columnGain: 'Gain',
    columnTrend: 'Trend',
    columnStatus: 'Verdict',
    disclaimer: 'This is monitoring, not a diagnosis. If in doubt, contact your vet.',
    /**
     * The five verdicts. Short enough to sit in a table cell, and worded so that
     * `above` cannot be mistaken for a warning — the research this model is built
     * on concludes explicitly that optimal growth is not maximal growth.
     */
    status: {
      alarm: 'Past the risk threshold',
      below: 'Below the expected range',
      normal: 'Growing properly',
      above: 'Growing faster than the range',
      insufficientData: 'Not enough to judge yet',
    },
    advice: {
      alarm: 'Contact your vet. Check that the puppy is nursing and warm, consider supplementing, and weigh every 6–8 hours until it turns around.',
      below: 'Watch it at every weighing. Check it reaches a teat and is not pushed off, check the nest temperature, and consider supplementing.',
      normal: 'Nothing to do.',
      above: 'Nothing to do.',
      insufficientData: 'Record another weighing.',
    },
    /** The regime the puppy's age puts the model in. See `GrowthPhase`. */
    phase: {
      earlyNeonatal: 'first 48 hours',
      neonatal: 'reference curve',
      postNeonatal: 'no published reference',
    },
    confidence: {
      high: 'high confidence',
      medium: 'medium confidence',
      low: 'low confidence',
    },
    /**
     * Said under an alarm the model is not sure of — and only there. Everything
     * else below high confidence is simply not shown, so these two lines exist
     * to stop a 4am phone call being made on evidence the reader thinks is
     * firmer than it is. There is no `high` entry: at high confidence nothing is
     * rendered, and an empty string in a phrase tree is a string somebody will
     * one day print.
     */
    confidenceNote: {
      medium: 'Read from fewer weighings than we would like, or from a rule with no published threshold behind it.',
      low: 'Read from very little: too few weighings, too short a gap between them, or an age this model has no reference for.',
    },
    /**
     * One line per rule that can fire, in the model's own words. The code is the
     * key; the two numbers it compared are printed underneath by `against`.
     *
     * Each says WHAT WAS SEEN, not what to do — the advice is one line, keyed by
     * the verdict, and repeating it per rule would give a puppy with three
     * findings three instructions.
     */
    reason: {
      gr01BelowThreshold: 'Lost more in the first 24 hours than newborns that go on to thrive.',
      gr01Negative: 'Lighter at 24 hours than at birth. Within the normal range, but worth a look.',
      gr12BelowThreshold: 'Lost weight through the second day, past the point associated with risk.',
      gr02BelowThreshold: 'Down on its birth weight at 48 hours by more than newborns that go on to thrive.',
      gr02BelowExpected: 'Still below its birth weight at 48 hours by more than expected.',
      lossVsBirth: 'Has lost more of its birth weight than a newborn should in its first two days.',
      awaitingFirstDay: 'Born within the last day and not weighed since.',
      sgrRatioAlarm: 'Growing far slower than the reference curve for this age.',
      sgrRatioBelow: 'Growing slower than the reference curve for this age.',
      sgrRatioNormal: 'Growing at the rate the reference curve expects.',
      sgrRatioAbove: 'Growing faster than the reference curve expects.',
      weightLoss24h: 'Lost weight over the last day.',
      weightLoss48h: 'No weight gained across the last two days. Past the second day of life that is not physiological.',
      mortalityRiskDay7: 'The first week added up to less than newborns that go on to thrive.',
      laggingLitter: 'Behind its littermates, measured from each one’s own birth weight.',
      heuristicPhase: 'Past three weeks there is no published reference curve; what follows is our own rule of thumb.',
      postWeightLoss: 'Lost weight over the last three days.',
      postSgrFloor: 'Barely gaining over the last three days.',
      litterRelativeBelow: 'Well behind its littermates, measured from each one’s own birth weight.',
      litterRelativeAbove: 'Well ahead of its littermates, measured from each one’s own birth weight.',
      litterDrift: 'Falling further behind its littermates week on week.',
      birthWeightCategoryUnknown:
        'We cannot tell whether this was a low birth weight, so the gentler thresholds are being used.',
      noBirthWeight: 'No birth weight recorded — the first-week checks are all measured from it.',
      notEnoughWeighings: 'Not enough weighings close enough together to read a trend.',
      staleWeighings: 'Read from weighings that are no longer fresh.',
      outOfModelRange: 'Past eight weeks. Weights are still recorded; they are no longer judged.',
    },
    /**
     * The theory behind the section, behind an info icon by its title: what is
     * compared with what, where the numbers come from, and — the part that
     * matters most — why the section is usually silent. Written for someone who
     * has never seen the app and needs no arithmetic. Every number in it is
     * interpolated from `DEFAULT_GROWTH_CONFIG`, so this copy cannot drift from
     * what the app actually does.
     */
    guide: {
      open: 'How growth is judged',
      title: 'How growth is judged',
      lead: 'Every puppy is judged against itself, and against what the research says a newborn of that age does. The question is always the same one: what fraction of its own weight has it gained, and over how long.',
      rateTitle: 'The rate is what matters',
      rateBody: 'Ten grams on a puppy weighing 100 g is a very good day. The same ten grams on a puppy weighing 500 g is a day after which you should look in on it. So every threshold here is a percentage of the puppy’s own body weight per day.',
      rateBreed: 'That percentage does not depend on the breed. A newborn gains about 13% of itself on its first day and about 6% by three weeks whether it will grow up to weigh three kilograms or forty — the breed changes the grams, not the percentage. It is the falling with age that matters, and that is what the curve below is.',
      windowTitle: 'What one verdict is read from',
      windowBody: 'Not two weighings, but all of them in the last {{hours}} hours — {{late}} hours once the puppies are over three weeks old and weaning starts. A line is fitted through those weights and its slope is the growth rate.',
      windowWhy: 'A newborn’s weight wobbles by three to eight per cent between one weighing and the next for reasons that have nothing to do with growth: a full stomach, a full bladder, a squirm on the scale. A real day’s gain is five to ten per cent. Over a single night the noise is the same size as the news, which is why a verdict read off one overnight interval is close to a coin toss — and why this one is not.',
      phasesTitle: 'Three ages, three different questions',
      phasesBody: 'The first two days are not a slower version of the first two weeks, and the second month is not a slower version of the first. Each has its own rules.',
      phaseAge: {
        earlyNeonatal: 'first {{early}} days',
        neonatal: 'days {{early}} to {{neonatal}}',
        postNeonatal: 'days {{neonatal}} to {{end}}',
      },
      phaseWhat: {
        earlyNeonatal:
          'Losing weight is normal here — the question is only how much. The thresholds are published ones, measured at 24 and 48 hours from the birth weight.',
        neonatal:
          'Growth is exponential with a rate that falls as the puppy ages, and there is a published curve for it. This is where the app is on its firmest ground, and the seventh day is checked against a threshold of its own.',
        postNeonatal:
          'No published reference curve exists for this stretch. The app falls back on the puppy’s own trend and on how it compares with its littermates, and never claims more than medium confidence about it.',
      },
      birthWeightTitle: 'Low birth weight, and why we ask about the dam',
      birthWeightBody: 'A small newborn tolerates far less of a loss in the first 48 hours than a normal-sized one, and is then expected to make more of it back. Those are two different sets of thresholds, and picking the wrong one either raises false alarms or misses real ones.',
      birthWeightDam: 'The reliable way to tell them apart, without a table for every breed, is the puppy’s weight as a percentage of the dam’s own — from {{toy}}% for a toy bitch down to {{giant}}% for a giant one. That is why the litter asks for what she weighed before the mating.',
      birthWeightFallback: 'Without it, the app falls back on the litter itself: a puppy under {{percent}}% of its littermates’ median birth weight, in a litter of at least {{count}}. That is our own rule of thumb rather than a published one, and when even that is unavailable the puppy is judged on the gentler thresholds and the section says less.',
      hourTitle: 'Why the hour of birth matters',
      hourBody: 'The first {{early}} days are judged in windows 24 hours wide. A date without a time puts the age out by up to twelve hours, which is half the window — so those checks are read off the wrong pair of weighings. An approximate hour is worth far more here than a precise midnight.',
      meaningTitle: 'What each verdict means',
      meaning: {
        alarm:
          'Past a threshold the research ties to raised newborn mortality, or losing weight past the second day of life. This is the one verdict shown however sure or unsure the app is of it — and when it is unsure, it says so underneath.',
        below:
          'Under the expected range but above the risk threshold. Shown only when the app is confident: enough weighings, close enough together, at an age it has a reference for.',
        normal: 'Growing at the rate expected for this age. Nothing to do.',
        above:
          'Growing faster than the range. Information and not a warning: the research this is built on concludes in as many words that optimal growth is not maximal growth. It is never listed as something to act on.',
        insufficientData:
          'The rules for this age could not be worked out — too few weighings in the window, no birth weight recorded, or a puppy past eight weeks. The weights are still saved and still drawn.',
      },
      confidenceTitle: 'Why this section is usually silent',
      confidenceBody: 'Every verdict carries how sure the app is of it, and most of the time that decides whether anything is said at all. A litter growing ordinarily produces no box above the chart — that is the section working, not the section broken.',
      confidenceWhat: {
        high: 'At least {{points}} weighings spanning {{hours}} hours or more, at an age with a published reference, on a litter whose birth-weight class could be established. This is the only level at which “below the range” is reported.',
        medium: 'Two weighings at least {{minHours}} hours apart, or an age past day {{end}} where the rules are our own rather than published, or a puppy whose birth-weight class is unknown.',
        low: 'Very little to go on: a gap too short or too long to read a rate out of, or a first day with nothing weighed yet.',
      },
      confidenceAlarm: 'An alarm is the exception and is always shown, at every level of confidence. It is the verdict tied to newborn mortality, and a quiet one would be the app choosing tidiness over the thing it is for.',
      corridorTitle: 'The band on the chart',
      corridorBody: 'The shaded band shows where the puppy should be, day by day, starting from what it actually weighed at {{anchor}} days old and growing from there at the reference rate. It runs to day {{end}} and stops, because the published curve stops there too.',
      corridorEdges: 'Its edges are the same two numbers the verdicts use: {{low}}% and {{high}}% of the reference rate sustained. So a line inside the band is a puppy inside the range, and the picture and the words cannot disagree.',
      sourcesTitle: 'Where the numbers come from',
      sourcesBody: 'Every threshold here is from a published study of real litters, or is marked as our own rule of thumb wherever it is shown.',
      sources: {
        alves: 'Alves (2020) — the growth curve for the first three weeks, from 345 puppies across 19 breeds.',
        mugnier: 'Mugnier and others (2023) — the risk thresholds at 24 hours, 48 hours and seven days, from 8550 puppies in 127 kennels.',
        mila: 'Mila and others (2015) — the 48 hour threshold for a normal-weight newborn.',
        alonge: 'Alonge and others (2023) — birth weight as a percentage of the dam’s, and what counts as low for her size.',
      },
      limitsLead: 'What it does not know:',
      limits: {
        population: 'The studies are of French, Italian and Portuguese kennels. Their authors themselves advise caution in carrying the numbers to other populations.',
        extrapolation: 'The reference curve is calibrated on days two to seven and carried forward to day {{end}}. That stretch is the weakest part of the model.',
        heuristic: 'Everything past day {{end}} is our own rule of thumb. No published thresholds exist for it.',
        supplementing: 'The app does not know whether a puppy is being bottle-fed. A supplemented puppy can read as growing properly while something underneath is wrong — a verdict is not a diagnosis.',
        firstWeighing: 'Whether the first weighing was before or after the first feed shifts the 24 hour figure by a few per cent. Weigh the same way every time and it stops mattering.',
      },
    },
  },
  /**
   * The weighing log under the chart: who weighed the litter, and when.
   *
   * Separate from `growth.*` because it is not a view of growth — it is the
   * record of what people did. One weighing covers the whole litter at once.
   * See `MeasurementLog.tsx`.
   */
  measurements: {
    title: 'Weighing history',
    lead: 'Who recorded the weights, and when. Correct or remove a whole weighing from here.',
    columnWhen: 'Weighing',
    columnCount: 'Puppies',
    // How much of the litter that weighing covered, out of the litter as it
    // stands today. Both halves, because a session may cover part of it.
    countOf: '{{weighed}} of {{total}}',
    columnWho: 'Recorded by',
    edited: 'Corrected by {{name}}, {{date}}',
    edit: 'Correct the weighing of {{date}}',
    delete: 'Delete the weighing of {{date}}',
    deleteTitle: 'Delete the whole weighing?',
    deleteBody_one: 'The weight of {{count}} puppy from {{date}} will be gone. This cannot be undone.',
    deleteBody_other: 'The weights of {{count}} puppies from {{date}} will be gone. This cannot be undone.',
    editTitle: 'Correct the weighing',
    editLead: 'Change the weights or the date of this weighing. Everyone in the kennel sees the change.',
    saveChanges: 'Save changes',
    discardChanges: 'Discard changes',
    discardChangesTitle: 'Discard these corrections?',
    discardChangesBody: 'The weighing stays as it was saved.',
    discardChangesCancel: 'Keep correcting',
  },
  /** The archive of photographings, beside the weighing log. See pl.ts. */
  photoLog: {
    title: 'Photo history',
    lead: 'Who added photographs of the litter, and when. Correct or remove a whole session from here.',
    columnWhen: 'Session',
    columnWho: 'Added by',
    edited: 'Corrected by {{name}}, {{date}}',
    edit: 'Correct the session of {{date}}',
    delete: 'Delete the session of {{date}}',
    deleteTitle: 'Delete the whole session?',
    deleteBody_one: '{{count}} photograph from {{date}} will be gone. This cannot be undone.',
    deleteBody_other: '{{count}} photographs from {{date}} will be gone. This cannot be undone.',
    editTitle: 'Correct the photo session',
    editLead: 'Change the photographs or the date of this session. Everyone in the kennel sees the change.',
    saveChanges: 'Save changes',
    discardChanges: 'Discard changes',
    discardChangesTitle: 'Discard these corrections?',
    discardChangesBody: 'The session stays as it was saved.',
    discardChangesCancel: 'Keep correcting',
  },
  /**
   * The archive read as an album rather than as an audit: the sessions down a
   * line, newest first, each one a day's photographs. Separate from `photoLog.*`,
   * which is the same collection read as a record of what people did. See
   * `components/gallery/PhotoTimeline.tsx`.
   */
  photoTimeline: {
    title: 'Photo timeline',
    // For a litter nobody has photographed yet. The button beside the heading
    // says what to do about it.
    empty: 'No photos of this litter yet',
    // The same line on one puppy's page, where it holds that puppy's own frames
    // and none of the litter's group shots. Photographs are added from the
    // litter's page, so there is no button here to say otherwise.
    emptySubject: 'No photos of this puppy yet',
    // On each card, under the date: how many photographs that afternoon
    // produced, the litter's own and the puppies' together.
    count_one: '{{count}} photograph',
    count_other: '{{count}} photographs',
    // The rest of the archive, already loaded and one tap away. The number is
    // what is still hidden, so the reader knows how much is behind it.
    showMore_one: 'Show {{count}} earlier session',
    showMore_other: 'Show {{count}} earlier sessions',
  },
  /** The photo wizard: one dated photographing of a litter. See pl.ts. */
  photos: {
    title: 'Adding photographs',
    progress_one: '{{count}} photograph',
    progress_other: '{{count}} photographs',
    today: 'Saved as photographs from today.',
    backdated: 'Saved as photographs from {{date}}.',
    litterLabel: 'The whole litter',
    litterHint: 'The nest, the whole group, the dam with her puppies.',
    offspringLabel: 'Each puppy',
    save: 'Save photographs',
    saveWaiting_one: 'Sending {{count}} photograph…',
    saveWaiting_other: 'Sending {{count}} photographs…',
    discard: 'Discard without saving',
    leaveTitle: 'Discard these photographs?',
    leaveBody: 'The photographs added so far will be gone. Nothing has been saved yet.',
    leaveCancel: 'Keep adding',
  },
  /**
   * One photograph: the add button, the upload strip, the open lightbox.
   * Separate from `photos.*`, which runs a whole session, and from
   * `photoTimeline.*`, which describes the line. See pl.ts.
   */
  gallery: {
    add: 'Add photos',
    remove: 'Remove photo',
    uploading: 'Sending…',
    failed: 'A photograph did not upload. Try again.',
    lightbox: {
      close: 'Close',
      zoom: 'Zoom',
      previous: 'Previous photo',
      next: 'Next photo',
      error: 'The photograph could not be loaded',
      thumbnail: 'Photo {{number}} of {{total}}',
      // The two links at the ends of the thumbnail strip. "Previous" and
      // "next" mean what they mean on the arrows above — the direction of
      // travel, not the order in time; the line starts at the newest session,
      // so the previous one is the newer. The day and date say which.
      previousSession: 'Previous session: {{day}}, {{date}}',
      nextSession: 'Next session: {{day}}, {{date}}',
    },
  },
  /** Photographs shared into the app from the phone's gallery. See pl.ts. */
  share: {
    title: 'Photos from the gallery',
    pick_one: 'You shared {{count}} photo. Which litter?',
    pick_other: 'You shared {{count}} photos. Which litter?',
    empty: 'There are no photos here',
    emptyHint: 'Add them from the litter’s album, with the Add photos button.',
    readOnly: 'Photos are added by the owner or the breeder.',
    readOnlyHint: 'Ask whoever runs the kennel.',
    noLitter: 'There is no litter for them to go to yet',
    noLitterHint: 'Start a litter, then share the photos again.',
    toLitters: 'Go to litters',
  },
  /** What is to be done to this litter, and what has been. See pl.ts. */
  tasks: {
    title: 'Tasks',
    add: 'New task',
    empty: 'Nothing written down yet.',
    emptyWhere: 'Write down what needs doing to this litter, and the day it is due.',
    textLabel: 'What needs doing',
    textPlaceholder: 'Worm the whole litter',
    needsText: 'Write down what needs doing.',
    dayLabel: 'Day of life',
    dateLabel: 'Due',
    dueHint: 'Type the puppies’ day of life or the date — the other one follows.',
    needsDate: 'Pick a date.',
    day: 'Day {{day}}',
    today: 'Today',
    tomorrow: 'Tomorrow',
    ahead_one: 'In {{count}} day',
    ahead_other: 'In {{count}} days',
    overdue_one: '{{count}} day overdue',
    overdue_other: '{{count}} days overdue',
    markDone: 'Tick off: {{text}}',
    markUndone: 'Put back: {{text}}',
    edit: 'Change task',
    delete: 'Delete task',
    deleteTitle: 'Delete this task?',
    deleteBody: '“{{text}}” goes off the list. This cannot be undone.',
    save: 'Save task',
    doneTitle: 'Done',
    doneBy: 'Done {{date}} — {{name}}',
  },
  settings: {
    language: 'Language',
    languageHint: 'Saved to your profile, so your phone and your tablet open in the same language.',
    displayName: 'Display name',
    displayNameHint: 'What the others in the kennel see beside your weights and notes.',
    displayNameFallback: 'Leave it empty and you will be shown as “{{name}}”.',
  },
  /** Autonyms, identical in every locale. See pl.ts. */
  language: {
    pl: 'Polski',
    en: 'English',
  },
  /** See pl.ts. */
  install: {
    title: 'The app on your home screen',
    hint: 'Add Puppy Doctor to your home screen and it opens full-screen, like an app.',
    action: 'Add to home screen',
    iosShare: 'Tap the share icon in the browser bar:',
    iosAdd: 'Choose “Add to Home Screen”.',
    iosConfirm: 'Confirm with “Add”.',
  },
  update: {
    available: 'A new version is available',
    reload: 'Reload',
  },
} as const;
